import { DEFAULT_STARGATE_SERVICE_URL, DIRECT_ACCESS_URL } from './constants';
import { fetchJson, postRequest, RespCodes } from './helpers';
import type {
	GetDirectAccessSettingFailureResponseValue,
	GetDirectAccessSettingRequest,
	GetDirectAccessSettingResponse,
	GetDirectAccessSettingSuccessResponseValue,
	UpdateDirectAccessSettingRequest,
	UpdateDirectAccessSettingResponse,
} from './types';
import { tryCacheWithClear, resolveError } from './utils';

interface InviteApiClient {
	getDirectAccessSetting(
		request: GetDirectAccessSettingRequest,
	): Promise<GetDirectAccessSettingResponse>;
	updateDirectAccessSettings(
		request: UpdateDirectAccessSettingRequest,
	): Promise<UpdateDirectAccessSettingResponse>;
}

export class InviteApiClientImpl implements InviteApiClient {
	directAccessSettingsCache: Map<
		string,
		GetDirectAccessSettingSuccessResponseValue | GetDirectAccessSettingFailureResponseValue
	>;

	constructor() {
		this.directAccessSettingsCache = new Map();
	}

	public clearDESCache = () => {
		this.directAccessSettingsCache.clear();
	};

	public async updateDirectAccessSettings({
		product,
		cloudId,
		location,
		domains,
	}: UpdateDirectAccessSettingRequest): Promise<UpdateDirectAccessSettingResponse> {
		const resource = `ari:cloud:${product}::site/${cloudId}`;
		const url = `${DEFAULT_STARGATE_SERVICE_URL}${DIRECT_ACCESS_URL}`;
		const request = {
			resource,
			domains,
			location,
			action: 'ACCEPT',
			setting: 'DIRECT_ACCESS',
		};
		await postRequest(url, 'updateDirectAccessSettings', request);
		return { success: true, updateDirectAccessSettings: true };
	}

	public async getDirectAccessSetting(
		request: GetDirectAccessSettingRequest,
	): Promise<GetDirectAccessSettingResponse> {
		const performFetch = () => {
			const url = `${DEFAULT_STARGATE_SERVICE_URL}${DIRECT_ACCESS_URL}`;
			const queryParams = `resource=${request.productAri}&domain=${
				request.domain
			}&setting=${request.setting}${request.roleAri ? `&role=${request.roleAri}` : ''}`;

			return fetchJson<GetDirectAccessSettingSuccessResponseValue>(
				`${url}?${queryParams}`,
				'getDirectAccessSetting',
			);
		};

		const cacheValue = this.directAccessSettingsCache.get(request.domain);
		if (cacheValue) {
			return { response: cacheValue, cached: true };
		}
		try {
			const responseJson = await performFetch();
			tryCacheWithClear(this.directAccessSettingsCache, request.domain, responseJson);
			return { response: responseJson, cached: false };
		} catch (err) {
			const { responseCode, errorMessage } = resolveError(err);
			if (
				responseCode &&
				responseCode >= RespCodes.BAD_REQUESTS &&
				responseCode <= RespCodes.CLIENT_CLOSED_REQUEST
			) {
				const failResponseJson: GetDirectAccessSettingFailureResponseValue = {
					domain: request.domain,
					errorMessage,
				};
				tryCacheWithClear(this.directAccessSettingsCache, request.domain, failResponseJson);
			}
			throw err;
		}
	}
}
